import React, { useState } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Feedback,
  BugReport,
  Lightbulb,
  RateReview,
  Close
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyId } from '../contexts/FamilyIdContext';

function FeedbackWidget() {
  const { currentUser, userProfile } = useAuth();
  const { familyId } = useFamilyId();
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('feedback'); // 'bug', 'feature', 'feedback', 'review'
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successSnackbar, setSuccessSnackbar] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setFeedbackType('feedback');
    setSubject('');
    setMessage('');
    setError('');
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Please enter your feedback.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const functions = getFunctions(getApp(), 'us-central1');
      const submitFeedbackFn = httpsCallable(functions, 'submitFeedback');
      await submitFeedbackFn({
        type: feedbackType,
        subject: subject || getFeedbackTypeLabel(feedbackType),
        message: message.trim(),
        email: email.trim(),
        userName: userProfile?.displayName || currentUser?.email || 'Anonymous',
        familyId: familyId || null
      });

      setSuccessSnackbar(true);
      handleClose();
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getFeedbackTypeLabel = (type) => {
    switch (type) {
      case 'bug': return 'Bug Report';
      case 'feature': return 'Feature Request';
      case 'review': return 'Review';
      default: return 'General Feedback';
    }
  };

  const getFeedbackTypeDescription = (type) => {
    switch (type) {
      case 'bug': return 'Found a bug? Let us know so we can fix it ASAP!';
      case 'feature': return 'Have an idea? We\'d love to hear your suggestions!';
      case 'review': return 'Share your experience with Earn-E Chore Helper!';
      default: return 'We value your feedback and read every message!';
    }
  };

  return (
    <>
      {/* Floating Feedback Button */}
      <Fab
        color="secondary"
        aria-label="feedback"
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: 80, // Above the add shopping item button
          left: 16,
          zIndex: 1000,
          boxShadow: 3
        }}
      >
        <Feedback />
      </Fab>

      {/* Feedback Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Send Feedback</Typography>
          <Button onClick={handleClose} color="inherit" size="small">
            <Close />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Feedback Type Selector */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                What type of feedback?
              </Typography>
              <ToggleButtonGroup
                value={feedbackType}
                exclusive
                onChange={(e, newType) => newType && setFeedbackType(newType)}
                aria-label="feedback type"
                fullWidth
                size="small"
              >
                <ToggleButton value="bug" aria-label="bug report">
                  <BugReport sx={{ mr: 0.5 }} fontSize="small" />
                  Bug
                </ToggleButton>
                <ToggleButton value="feature" aria-label="feature request">
                  <Lightbulb sx={{ mr: 0.5 }} fontSize="small" />
                  Feature
                </ToggleButton>
                <ToggleButton value="feedback" aria-label="general feedback">
                  <Feedback sx={{ mr: 0.5 }} fontSize="small" />
                  Feedback
                </ToggleButton>
                <ToggleButton value="review" aria-label="review">
                  <RateReview sx={{ mr: 0.5 }} fontSize="small" />
                  Review
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Description */}
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                {getFeedbackTypeDescription(feedbackType)}
              </Typography>
            </Alert>

            {/* Subject (optional) */}
            <TextField
              label="Subject (optional)"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder={`e.g., "Barcode scanner not working" or "Add recurring weekly tasks"`}
            />

            {/* Message */}
            <TextField
              label={feedbackType === 'bug' ? 'Describe the bug' : 'Your message'}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              required
              placeholder={
                feedbackType === 'bug'
                  ? 'What happened? What were you trying to do? Any error messages?'
                  : feedbackType === 'feature'
                  ? 'Describe your feature idea and how it would help you...'
                  : 'Tell us what you think...'
              }
            />

            {/* Email */}
            <TextField
              label="Email (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              variant="outlined"
              type="email"
              helperText="We'll only use this to follow up on your feedback"
            />

            {error && <Alert severity="error">{error}</Alert>}

            {feedbackType === 'bug' && (
              <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
                🚨 <strong>Urgent bugs:</strong> We typically respond within 24 hours. Include screenshots if possible!
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !message.trim()}
            startIcon={submitting ? <CircularProgress size={20} /> : <Feedback />}
          >
            {submitting ? 'Sending...' : 'Send Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbar}
        autoHideDuration={4000}
        onClose={() => setSuccessSnackbar(false)}
        message="✅ Thank you! Your feedback has been received."
      />
    </>
  );
}

export default FeedbackWidget;













